import { TailoringModeProvider } from "@/context/TailoringModeContext";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import TailoringProgressPill from "./components/TailoringProgressPill";
import { useAuth, AuthProvider } from "@/hooks/useAuth";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import ResumeBuilder from "./pages/ResumeBuilder";
import DocumentEditor from "./pages/DocumentEditor";
import ResumeWizard from "./pages/ResumeWizard";
import ResumeResults from "./pages/ResumeResults";
import ResumeResultsTemplated from "./pages/ResumeResultsTemplated";
import ResumeAnalysisDetail from "./pages/ResumeAnalysisDetail";
import SavedResumeDetail from "./pages/SavedResumeDetail";
import LinkedInWizard from "./pages/LinkedInWizard";
import LinkedInWizardLanding from "./pages/LinkedInWizardLanding";
import LinkedInQuickWizard from "./pages/LinkedInQuickWizard";
import LinkedInWizardSample from "./pages/LinkedInWizardSample";
import LinkedInProfileLibrary from "./components/LinkedInProfileLibrary";
import LinkedInProfileDetail from "./pages/LinkedInProfileDetail";
import ResumeQuantification from "./pages/ResumeQuantification";
import MetricsWizardLanding from "./pages/MetricsWizardLanding";
import CoverLetterBuilder from "./pages/CoverLetterBuilder";
import BulkLetterGenerator from "./pages/BulkLetterGenerator";
import BulkResumeGenerator from "./pages/BulkResumeGenerator";
import ResumeDocmosisBuilder from "./pages/ResumeDocmosisBuilder";
import About from "./pages/About";
import WatchDemo from "./pages/WatchDemo";
import FAQ from "./pages/FAQ";
import Services from "./pages/Services";
import Blog from "./pages/Blog";
import Careers from "./pages/Careers";
import AdminLogin from "./pages/AdminLogin";
import { AdminLayout, AdminOverview, AdminUsers, AdminUserDetail, AdminBilling, AdminActivity, AdminSettings, AdminTemplates, AdminPipelineBuilder, AdminLinkTracking } from "./pages/admin";
import Profile from "./pages/Profile";
import Reviews from "./pages/Reviews";
import HowItWorks from "./pages/HowItWorks";
import Features from "./pages/Features";
import Tools from "./pages/Tools";
import HowItWorksPage from "./pages/HowItWorksPage";
import TailorWizVsChatGPT from "./pages/TailorWizVsChatGPT";
import PricingPage from "./pages/PricingPage";
import Benefits from "./pages/Benefits";
import ContactPage from "./pages/ContactPage";
import Signup from "./pages/Signup";
import BuyCredits from "./pages/BuyCredits";
import PaymentReceived from "./pages/PaymentReceived";
import FreeEvaluation from "./pages/FreeEvaluation";
import ResumePackages from "./pages/ResumePackages";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import HelpCenter from "./pages/HelpCenter";
import SolutionAsset from "./pages/SolutionAsset";

import PDFExtractTest from "./pages/PDFExtractTest";
import FindHiringManager from "./pages/FindHiringManager";
import HiringManagerReports from "./pages/HiringManagerReports";
import HiringManagerId from "./pages/HiringManagerId";
import HiringManagerId2 from "./pages/HiringManagerId2";
import JobLegitimacyReports from "./pages/JobLegitimacyReports";
import CareerClarityReports from "./pages/CareerClarityReports";
import CompanyInsights from "./pages/CompanyInsights";
import CompanyInsightsReports from "./pages/CompanyInsightsReports";
import CareerClarityTool from "./pages/CareerClarityTool";
import JobFinderHub from "./pages/JobFinderHub";
import JobCheck from "./pages/JobCheck";

import InterviewMagic from "./pages/InterviewMagic";
import WordDocumentViewer from "./pages/WordDocumentViewer";
import DocumentGenerator from "./pages/DocumentGenerator";
import VectorShiftDirectTest from "./pages/VectorShiftDirectTest";
import BioPreviewTest from "./pages/BioPreviewTest";
import LinkedInPackagePreview from "./pages/LinkedInPackagePreview";
import DocumentScrapingTest from "./pages/DocumentScrapingTest";
import VectorShiftFileTest from "./pages/VectorShiftFileTest";
import ResumeProcessing from "./pages/ResumeProcessing";
import PublicResumeViewer from "./pages/PublicResumeViewer";
import ClarissaResume from "./pages/ClarissaResume";
import VectorShiftOutputTest from "./pages/VectorShiftOutputTest";
import TailoredDocumentDetail from "./pages/TailoredDocumentDetail";
import JSONPersistenceTest from "./pages/JSONPersistenceTest";
import FileNamingTest from "./pages/FileNamingTest";
import CustomTemplateGenerator from "./pages/CustomTemplateGenerator";

import JobIntelSalesPage from "./pages/JobIntelSalesPage";
import JobQualificationCheck from "./pages/JobQualificationCheck";
import ResumeRevisions from "./pages/ResumeRevisions";

import AdminPipelinesManagement from "./pages/AdminPipelinesManagement";
import AdminPromptsManagement from "./pages/AdminPromptsManagement";
import ResumeTailoringTest from "./pages/ResumeTailoringTest";
import ResumeEditorNew from "./pages/ResumeEditorNew";
import ResumeTailoringFixedDuplicate from "./pages/ResumeTailoringFixedDuplicate";
import ResumeTailoringClaudeDirect from "./pages/ResumeTailoringClaudeDirect";
import ResumeTailoringClaudeV1 from "./pages/ResumeTailoringClaudeV1";
import { ResumeTailoringRouter } from "./pages/ResumeTailoringSimple/ResumeTailoringRouter";
import ResumeWritingRulebook from "./pages/ResumeWritingRulebook";
import ResumeTemplates from "./pages/ResumeTemplates";
import ResumeTemplatesGallery from "./pages/ResumeTemplatesGallery";
import ResumeDesign from "./pages/ResumeDesign";
import DocmosisJsonTest from "./pages/DocmosisJsonTest";
import VectorShiftFormatTest from "./pages/VectorShiftFormatTest";
import JsonGenerator from "./pages/JsonGenerator";
import CreditHistory from "./pages/CreditHistory";
import JobIntelReport from "./pages/JobIntelReport";
import JobIntelReports from "./pages/JobIntelReports";
import JobIntel360ReportsHub from "./pages/JobIntel360ReportsHub";
import ResumeJobMatchReport from "./pages/ResumeJobMatchReport";
import SalaryIntelReport from "./pages/SalaryIntelReport";
import SalaryIntelReports from "./pages/SalaryIntelReports";

import SolutionAssetReports from "./pages/SolutionAssetReports";
import JobSearchTrackingHub from "./pages/JobSearchTrackingHub";
import InnerCircleReports from "./pages/InnerCircleReports";
import InnerCircleReport from "./pages/InnerCircleReport";
import JobIntelCreditHistory from "./pages/JobIntelCreditHistory";
import JobIntelProfileSetup from "./pages/JobIntelProfileSetup";
import JobIntelProfileEdit from "./pages/JobIntelProfileEdit";
import ClaudeRawTest from "./pages/ClaudeRawTest";
const queryClient = new QueryClient();

// The tailoring page stays MOUNTED across route changes (hidden with
// display:none) so an in-flight tailoring run keeps going while the user
// works elsewhere in the app; the floating TailoringProgressPill brings them
// back, and the run's own completion still navigates to the results page.
function PersistentTailoringPage() {
  const location = useLocation();
  const onPage = location.pathname === "/resume-tailoring-claude-direct";
  return (
    <div style={onPage ? undefined : { display: "none" }}>
      <ResumeTailoringClaudeDirect />
    </div>
  );
}

// MAINTENANCE (Marcus, Oct 25 2026): TailorWiz is closed to everyone except
// Marcus's own accounts until it is fixed. The sign-in page stays open so he
// can log in. The server functions enforce the same rule.
const MAINTENANCE_ALLOWED = new Set([
  "0b2c16a1-4eaa-48ff-b546-317d3917f3d2", // marcus@tailorwiz.com
  "32ba7e12-51a0-4aee-a837-0fc530ba4747", // marcushall2023@gmail.com
]);

function MaintenancePage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full text-center">
        <div className="text-[34px] font-extrabold tracking-tight" style={{ color: "#000F36" }}>
          Tailor<span style={{ color: "#C9A84C" }}>Wiz</span>
        </div>
        <h1 className="mt-6 text-2xl font-bold" style={{ color: "#000F36" }}>We're making TailorWiz better</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
          TailorWiz is getting an upgrade and will be back on <span className="font-semibold text-slate-800">Friday, October 30, 2026</span>.
          Your account, resumes and credits are safe and waiting for you.
        </p>
        <p className="mt-4 text-sm text-slate-500">Thank you for your patience.</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!(user?.id && MAINTENANCE_ALLOWED.has(user.id)) && location.pathname !== "/auth") {
    return <MaintenancePage />;
  }

  return (
    <>
    <PersistentTailoringPage />
    <TailoringProgressPill />
    <Routes>
      <Route
        path="/auth"
        element={
          user && user.email && !user.is_anonymous 
            ? <Navigate to="/" replace /> 
            : <Auth />
        } 
      />
      <Route path="/" element={<Index />} />
      <Route path="/dashboard" element={<Index />} />
      <Route path="/resume-builder" element={<ResumeBuilder />} />
      <Route path="/resume-editor-new" element={<ResumeEditorNew />} />
      <Route path="/document-editor" element={<DocumentEditor />} />
      {/* PERMANENT: the old Fixed Duplicate page redirects to Claude Direct.
          Catches every stale bookmark, cached link, and hardcoded navigate(). */}
      <Route path="/resume-tailoring-fixed-duplicate" element={<Navigate to="/resume-tailoring-claude-direct" replace />} />
      {/* Rendered by PersistentTailoringPage above so the run survives
          navigation; the route itself just needs to match. */}
      <Route path="/resume-tailoring-claude-direct" element={<></>} />
      <Route path="/bio-preview" element={<BioPreviewTest />} />
      <Route path="/li-package-preview" element={<LinkedInPackagePreview />} />
      <Route path="/resume-tailoring-claude-v1" element={<ResumeTailoringClaudeV1 />} />
        <Route path="/resume-tailoring-test" element={<ResumeTailoringTest />} />
        <Route path="/resume-tailoring-simple/*" element={<ResumeTailoringRouter />} />
      <Route path="/resume-wizard" element={<ResumeWizard />} />
      <Route path="/resume-results" element={<ResumeResults />} />
      <Route path="/resume-results-templated" element={<ResumeResultsTemplated />} />
      <Route path="/resume-processing" element={<ResumeProcessing />} />
      <Route path="/resume-analysis/:id" element={<ResumeAnalysisDetail />} />
      <Route path="/saved-resume/:id" element={<SavedResumeDetail />} />
      <Route path="/linkedin-wizard-landing" element={<LinkedInWizardLanding />} />
      <Route path="/linkedin-wizard" element={<LinkedInWizard />} />
      <Route path="/linkedin-quick-wizard" element={<LinkedInQuickWizard />} />
      <Route path="/linkedin-wizard-sample" element={<LinkedInWizardSample />} />
      <Route path="/linkedin-profiles" element={<LinkedInProfileLibrary />} />
      <Route path="/linkedin-profile/:profileId" element={<LinkedInProfileDetail />} />
      <Route path="/resume-metrics" element={<ResumeQuantification />} />
        <Route path="/metrics-wizard" element={<MetricsWizardLanding />} />
        <Route path="/qualification-wizard" element={<JobQualificationCheck />} />
        <Route path="/resume-revisions" element={<ResumeRevisions />} />
      <Route path="/find-hiring-manager" element={<FindHiringManager />} />
      <Route path="/hiring-manager-id" element={<HiringManagerId />} />
      <Route path="/hiring-manager-id2" element={<HiringManagerId2 />} />
      <Route path="/hiring-manager-reports" element={<HiringManagerReports />} />
      <Route path="/job-legitimacy-reports" element={<JobLegitimacyReports />} />
      <Route path="/career-clarity-reports" element={<CareerClarityReports />} />
      <Route path="/company-insights" element={<CompanyInsights />} />
      <Route path="/company-insights-reports" element={<CompanyInsightsReports />} />
      <Route path="/career-clarity-report" element={<CareerClarityTool />} />
          <Route path="/job-check" element={<JobCheck />} />
          
        <Route path="/interview-magic" element={<InterviewMagic />} />
      <Route path="/job-finder-hub" element={<JobFinderHub />} />
      <Route path="/find-track-jobs" element={<Navigate to="/job-finder-hub" replace />} />
      <Route path="/cover-letter-builder" element={<CoverLetterBuilder />} />
      <Route path="/bulk-letter-generator" element={<BulkLetterGenerator />} />
      <Route path="/bulk-resume-generator" element={<BulkResumeGenerator />} />
      <Route path="/custom-template-generator" element={<CustomTemplateGenerator />} />
      <Route path="/jobintel-suite" element={<JobIntelSalesPage />} />
      <Route path="/job-intel" element={<JobIntelReport />} />
      <Route path="/job-intel-reports" element={<JobIntelReports />} />
      <Route path="/jobintel-360-reports" element={<JobIntel360ReportsHub />} />
      <Route path="/resume-job-match" element={<ResumeJobMatchReport />} />
      <Route path="/salary-intel" element={<SalaryIntelReport />} />
      <Route path="/salary-intel-reports" element={<SalaryIntelReports />} />
      <Route path="/resume-docmosis-builder" element={<ResumeDocmosisBuilder />} />
      <Route path="/solution-asset-create" element={<Navigate to="/solution-asset" replace />} />
      <Route path="/solution-asset-reports" element={<SolutionAssetReports />} />
      <Route path="/job-search-tracking-hub" element={<JobSearchTrackingHub />} />
      <Route path="/inner-circle" element={<InnerCircleReport />} />
      <Route path="/inner-circle-reports" element={<InnerCircleReports />} />
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/services" element={<Services />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/watch" element={<WatchDemo />} />
      <Route path="/tailorwiz-vs-chatgpt" element={<TailorWizVsChatGPT />} />
      <Route path="/how-it-works-legacy" element={<HowItWorks />} />
      <Route path="/benefits" element={<Benefits />} />
      <Route path="/reviews" element={<Reviews />} />
      <Route path="/features" element={<Features />} />
      <Route path="/tools" element={<Tools />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/pricing-legacy" element={<Navigate to="/pricing" replace />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/buy-credits" element={<BuyCredits />} />
          <Route path="/membership" element={<Navigate to="/pricing" replace />} />
          <Route path="/payment-received" element={<PaymentReceived />} />
          <Route path="/free-evaluation" element={<FreeEvaluation />} />
          <Route path="/resume-packages" element={<ResumePackages />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/contact-legacy" element={<Contact />} />
      <Route path="/help-center" element={<HelpCenter />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/solution-asset" element={<SolutionAsset />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/credit-history" element={<CreditHistory />} />
      <Route path="/jobintel-credit-history" element={<JobIntelCreditHistory />} />
      <Route path="/jobintel-profile-setup" element={<JobIntelProfileSetup />} />
      <Route path="/jobintel-profile-edit" element={<JobIntelProfileEdit />} />
      <Route path="/json-persistence-test" element={<JSONPersistenceTest />} />
      <Route path="/file-naming-test" element={<FileNamingTest />} />
      <Route path="/pdf-extract-test" element={<PDFExtractTest />} />
      <Route path="/word-document-viewer" element={<WordDocumentViewer />} />
          <Route path="/document-generator" element={<DocumentGenerator />} />
      <Route path="/vectorshift-direct-test" element={<VectorShiftDirectTest />} />
      <Route path="/document-scraping-test" element={<DocumentScrapingTest />} />
      <Route path="/vectorshift-file-test" element={<VectorShiftFileTest />} />
      <Route path="/vectorshift-output-test" element={<VectorShiftOutputTest />} />
      <Route path="/public-resume/:resumeId" element={<PublicResumeViewer />} />
      <Route path="/clarissa-resume" element={<ClarissaResume />} />
      <Route path="/tailored-document/:sessionId" element={<TailoredDocumentDetail />} />
      <Route path="/resume-writing-rulebook" element={<ResumeWritingRulebook />} />
      <Route path="/resume-templates" element={<ResumeTemplates />} />
      <Route path="/resume-gallery" element={<ResumeTemplatesGallery />} />
      <Route path="/resume-design" element={<ResumeDesign />} />
      <Route path="/docmosis-json-test" element={<DocmosisJsonTest />} />
      <Route path="/vectorshift-format-test" element={<VectorShiftFormatTest />} />
      <Route path="/json-generator" element={<JsonGenerator />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:userId" element={<AdminUserDetail />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="activity" element={<AdminActivity />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="link-tracking" element={<AdminLinkTracking />} />
        <Route path="pipeline-builder" element={<AdminPipelineBuilder />} />
        <Route path="pipelines" element={<AdminPipelinesManagement />} />
        <Route path="prompts" element={<AdminPromptsManagement />} />
        <Route path="templates" element={<AdminTemplates />} />
      </Route>
      <Route path="/claude-raw-test" element={<ClaudeRawTest />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <TailoringModeProvider>
        <Toaster />
        <Sonner />
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
          <AppRoutes />
        </div>
        </TailoringModeProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
